import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek
} from 'date-fns';
import { getFlight, getFlightHistory } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { DELAY_CODE_OPTIONS } from '../constants/delayCodes';
import flightDataIcon from '../assets/icons/flightdata.svg';
import fuelUpliftIcon from '../assets/icons/fueluplift.svg';
import oilIcon from '../assets/icons/oil.svg';
import deicingIcon from '../assets/icons/deicing.svg';
import arrowLeftIcon from '../assets/icons/arrow-sm-left.svg';
import './OnGroundMode.css';
import './HistoricAircraftData.css';

const toDate = value => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const fmtDate = value => {
  const date = toDate(value);
  return date ? format(date, 'dd.MMM.yyyy') : '--';
};

const fmtShortDate = value => {
  const date = toDate(value);
  if (!date) return '--';
  return `${String(date.getUTCDate()).padStart(2, '0')} ${format(date, 'MMM')}`;
};

const fmtDateTime = value => {
  const date = toDate(value);
  if (!date) return '--';
  return `${String(date.getUTCDate()).padStart(2, '0')} ${format(date, 'MMM')} ${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}Z`;
};

const fmtTime = value => {
  const date = toDate(value);
  return date ? `${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}Z` : '--';
};

const num = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const fuelTotal = item => {
  if (!item) return 0;
  return num(item.departure_fuel_total) ||
    num(item.departure_fuel_left) + num(item.departure_fuel_center) + num(item.departure_fuel_right) + num(item.departure_fuel_other) ||
    num(item.arrival_fuel_left) + num(item.arrival_fuel_center) + num(item.arrival_fuel_right) + num(item.arrival_fuel_other);
};

  const minutesToText = minutes => {
  const total = Math.max(0, Math.round(Number(minutes) || 0));
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

const formatDelayReason = delay => {
  const option = DELAY_CODE_OPTIONS.find(item => item.code === String(delay?.delay_code || ''));
  const reason = delay?.delay_description || option?.reason || option?.description || '';
  return [delay?.delay_code, reason].filter(Boolean).join(' - ');
};

const getHistoricStatusInfo = status => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'completed') return { label: 'COMPLETED', className: 'completed' };
  if (normalized === 'in_progress' || normalized === 'post_flight') return { label: 'IN FLIGHT', className: 'in-flight' };
  if (normalized === 'cancelled') return { label: 'CANCELLED', className: 'cancelled' };
  if (normalized === 'planned') return { label: 'PLANNED', className: 'planned' };
  return { label: normalized ? normalized.replace(/_/g, ' ').toUpperCase() : 'UNKNOWN', className: 'unknown' };
};

const formatFuelValue = value => {
  const rounded = Math.round((Number(value) || 0) * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

const AUTOLAND_WIND_REGEX = /^(?:[0-2]\d{2}|3[0-5]\d|VRB)\/\d{2}$/i;
const AUTOLAND_TIME_REGEX = /^(?:[01]\d|2[0-4]):[0-5]\dZ$/;
const AUTOLAND_TEMP_REGEX = /^-?\d{2}C$/i;
const AUTOLAND_RUNWAY_REGEX = /^\d{2}[CRL]?$/i;

const defaultFilters = {
  search: '',
  status: 'historic',
  date_from: '',
  date_to: ''
};
const CALENDAR_WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const TIMELINE_DRAG_THRESHOLD = 18;
const TIMELINE_CLICK_SUPPRESS_SCROLL_THRESHOLD = 24;
const TIMELINE_DATE_MASK_WIDTH = 190;

const TimelineStamp = ({ date, user }) => (
  <div className="historic-stamp">
    <span>{fmtDateTime(date)}</span>
    <i />
    <span>{user || '--'}</span>
  </div>
);

const Chevron = () => <span className="ogm-chevron" aria-hidden="true">›</span>;

const SummaryTask = ({ label, statusText, isComplete, iconSrc, iconClassName = '', onClick }) => (
  <button type="button" className={`ogm-item historic-task-card ${isComplete ? 'complete' : 'incomplete'}`} onClick={onClick}>
    <div className="ogm-left">
      <span className="ogm-icon-placeholder" aria-hidden="true">
        {iconSrc ? <img src={iconSrc} alt="" className={`ogm-icon-img app-icon ${iconClassName}`.trim()} /> : null}
      </span>
      <span>{label}</span>
    </div>
    <div className="ogm-right">
      {statusText ? <span className="ogm-status-text">{statusText}</span> : null}
      <Chevron />
    </div>
  </button>
);

const HistoricDateSelector = ({
  fieldKey,
  label,
  value,
  isOpen,
  visibleMonth,
  onToggle,
  onChangeMonth,
  onSelectDate,
  onClear
}) => {
  const selectedDate = value ? parseISO(value) : null;
  const monthStart = startOfMonth(visibleMonth);
  const calendarDays = eachDayOfInterval({
    start: startOfWeek(monthStart, { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(monthStart), { weekStartsOn: 1 })
  });

  return (
    <div className={`historic-calendar-field ${fieldKey === 'date_to' ? 'align-right' : 'align-left'} ${isOpen ? 'open' : ''}`}>
      <button type="button" className="historic-calendar-trigger" onClick={() => onToggle(fieldKey)}>
        <span className={value ? 'has-value' : ''}>{value ? format(parseISO(value), 'dd.MMM.yyyy') : label}</span>
        <span className={`historic-calendar-chevron ${isOpen ? 'open' : ''}`}>▾</span>
      </button>

      <div className={`historic-calendar-popover ${isOpen ? 'open' : ''}`}>
        <div className="historic-calendar-head">
          <button type="button" className="historic-calendar-nav" onClick={() => onChangeMonth(fieldKey, -1)}>‹</button>
          <span className="historic-calendar-title">{format(visibleMonth, 'MMMM yyyy')}</span>
          <button type="button" className="historic-calendar-nav" onClick={() => onChangeMonth(fieldKey, 1)}>›</button>
        </div>
        <div className="historic-calendar-weekdays">
          {CALENDAR_WEEKDAYS.map(day => <span key={day}>{day}</span>)}
        </div>
        <div className="historic-calendar-grid">
          {calendarDays.map(day => (
            <button
              key={day.toISOString()}
              type="button"
              className={[
                'historic-calendar-day',
                isSameMonth(day, visibleMonth) ? 'current-month' : 'outside-month',
                selectedDate && isSameDay(day, selectedDate) ? 'selected' : ''
              ].join(' ')}
              onClick={() => onSelectDate(fieldKey, day)}
            >
              {format(day, 'd')}
            </button>
          ))}
        </div>
        <div className="historic-calendar-footer">
          <button type="button" className="historic-calendar-clear" onClick={() => onClear(fieldKey)}>Clear</button>
        </div>
      </div>
    </div>
  );
};

const ReadOnlyInput = ({ value, className = '', suffix }) => (
  suffix ? (
    <div className={`historic-readonly-suffix ${className}`}>
      <input className="form-input" readOnly value={value || ''} />
      <span>{suffix}</span>
    </div>
  ) : (
    <input className={`form-input ${className}`} readOnly value={value || ''} />
  )
);

const HistoricSignatureCard = ({ title, event, fallbackDate, fallbackUser, alt }) => (
  <>
    <TimelineStamp date={event?.created_at || fallbackDate} user={event?.performed_by_name || fallbackUser} />
    <section className="historic-workstep expanded">
      <div className="historic-workstep-head">
        <span className="historic-workstep-icon">✓</span>
        {title}
        <span />
      </div>
      <div className="historic-workstep-body">
        <div className="historic-accept-panel">
          <div className="historic-accept-date">
            {event?.created_at ? fmtTime(event.created_at) : '--'}
            <span>Date</span>
          </div>
          <div className="historic-signature-shell">
            <div className="historic-signature-box">
              {event?.signature_data_url ? (
                <img src={event.signature_data_url} alt={alt} />
              ) : (
                <>Signature not recorded</>
              )}
              <span></span>
            </div>
          </div>
        </div>
      </div>
    </section>
  </>
);

const HistoricAircraftData = ({ refreshSignal = 0 }) => {
  const { aircraft } = useAuth();
  const [filters, setFilters] = useState(defaultFilters);
  const [flights, setFlights] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeSummaryView, setActiveSummaryView] = useState(null);
  const [openCalendar, setOpenCalendar] = useState(null);
  const [timelineDate, setTimelineDate] = useState('');
  const [timelineDragging, setTimelineDragging] = useState(false);
  const [calendarMonths, setCalendarMonths] = useState({
    date_from: startOfMonth(new Date()),
    date_to: startOfMonth(new Date())
  });
  const pageRef = useRef(null);
  const timelineRef = useRef(null);
  const calendarWrapRef = useRef(null);
  const timelineDragRef = useRef({ active: false, moved: false, startX: 0, scrollLeft: 0 });
  const timelineDragFrameRef = useRef(0);
  const timelineDragTargetRef = useRef({ node: null, scrollLeft: 0 });
  const suppressTimelineCardClickRef = useRef(false);
  const scrollPositionsRef = useRef({});
  const selectedDetailRef = useRef(null);

  const selectedFlight = selectedDetail?.flight || flights.find(f => f.id === selectedId) || null;
  const acceptanceEvent = selectedDetail?.flightEvents?.find(event => event.event_type === 'ACCEPT_AIRCRAFT');
  const closeFlightEvent = selectedDetail?.flightEvents?.find(event => event.event_type === 'CLOSE_FLIGHT');
  const deicingRecords = selectedDetail?.deicingRecords || [];
  const fuelUplifts = selectedDetail?.fuelUplifts || [];
  const fuelData = selectedDetail?.fuelData || null;
  const latestFuel = fuelUplifts[fuelUplifts.length - 1] || null;
  const oil = selectedDetail?.oil || null;
  const autoland = selectedDetail?.autoland || null;
  const arrivalFuel = selectedDetail?.arrivalFuel || null;
  const delays = selectedDetail?.delays || [];
  const scheduledDepartureDate = toDate(selectedFlight?.scheduled_time_departure);
  const offBlockDate = toDate(selectedFlight?.time_off_block);
  const calculatedDepartureDelayMinutes = scheduledDepartureDate && offBlockDate
    ? Math.max(0, Math.round((offBlockDate.getTime() - scheduledDepartureDate.getTime()) / 60000))
    : 0;
  const flightUserName = selectedFlight?.pilot_name || selectedFlight?.user_name || selectedFlight?.full_name || acceptanceEvent?.performed_by_name || '--';
  const hasDeicing = deicingRecords.length > 0 || Boolean(selectedDetail?.deicing);
  const hasFlightData = Boolean(
    selectedFlight?.scheduled_time_departure ||
    selectedFlight?.time_off_block ||
    selectedFlight?.time_takeoff ||
    selectedFlight?.time_landing ||
    selectedFlight?.time_on_block
  );
  const hasFuel = Boolean(fuelData || fuelUplifts.length);
  const hasOil = Boolean(oil);
  const hasAutoland = autoland && String(autoland.status || '').toLowerCase() !== 'no' && (autoland.performed || autoland.status);
  const hasArrivalFuel = Boolean(arrivalFuel);
  const hasDelay = delays.length > 0;
  const crewMembers = (selectedDetail?.crew || []).filter(member => (member?.full_name || '').trim() !== '');
  const captainIndex = crewMembers.findIndex(member => /(captain|cpt|pic|commander)/i.test(member.title || ''));
  const firstOfficerIndex = crewMembers.findIndex((member, idx) =>
    idx !== captainIndex && /(first officer|fo\b|co[- ]?pilot|copilot)/i.test(member.title || '')
  );
  const captainName = crewMembers[captainIndex >= 0 ? captainIndex : 0]?.full_name || '';
  const firstOfficerName = crewMembers[firstOfficerIndex >= 0 ? firstOfficerIndex : crewMembers.findIndex((_, idx) => idx !== (captainIndex >= 0 ? captainIndex : 0))]?.full_name || '';
  const hasValue = value => value !== '' && value !== null && value !== undefined && value !== '--';
  const getAutolandFieldClass = isComplete => `autoland-field-control ${isComplete ? 'complete' : 'incomplete'}`;
  const autolandTimeValue = autoland?.report_time || fmtTime(selectedFlight?.time_landing);
  const autolandRegAndFlight = `${aircraft?.registration || ''}${selectedFlight?.flight_number ? ` / ${selectedFlight.flight_number}` : ''}`;
  const autolandAirport = selectedFlight?.to_airport || '';
  const autolandDate = selectedFlight?.date_of_flight ? fmtDate(selectedFlight.date_of_flight) : '';
  const autolandTimeIsValid = AUTOLAND_TIME_REGEX.test(autolandTimeValue);
  const autolandRunwayIsValid = AUTOLAND_RUNWAY_REGEX.test(autoland?.runway || '');
  const autolandWindIsValid = AUTOLAND_WIND_REGEX.test(autoland?.wind || '');
  const autolandTempIsValid = AUTOLAND_TEMP_REGEX.test(autoland?.temp || '');
  const autolandQnhIsValid = ((autoland?.qnh || '').match(/\d/g) || []).length >= 3;

  useEffect(() => {
    if (!aircraft?.id) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await getFlightHistory({
          aircraft_id: aircraft.id,
          status: filters.status,
          date_from: filters.date_from || undefined,
          date_to: filters.date_to || undefined,
          search: filters.search || undefined,
          limit: 120
        });
        if (cancelled) return;
        const rows = res.data.flights || [];
        setFlights(rows);
        setSelectedId(current => current && rows.some(f => f.id === current) ? current : rows[0]?.id || null);
      } catch (err) {
        console.error('Error loading historic flights:', err);
        if (!cancelled) setFlights([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const timeout = setTimeout(load, 180);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [aircraft?.id, filters, refreshSignal]);

  useEffect(() => {
    if (!selectedId) {
      setSelectedDetail(null);
      selectedDetailRef.current = null;
      return;
    }
    let cancelled = false;
    const loadDetail = async () => {
      const loadedFlightId = selectedDetailRef.current?.flight?.id;
      setDetailLoading(!loadedFlightId || String(loadedFlightId) !== String(selectedId));
      try {
        const res = await getFlight(selectedId);
        if (!cancelled) {
          selectedDetailRef.current = res.data;
          setSelectedDetail(res.data);
        }
      } catch (err) {
        console.error('Error loading historic flight detail:', err);
        if (!cancelled && !selectedDetailRef.current) setSelectedDetail(null);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };
    loadDetail();
    return () => {
      cancelled = true;
    };
  }, [selectedId, refreshSignal]);

  useEffect(() => {
    setActiveSummaryView(null);
  }, [selectedId]);

  useEffect(() => {
    const handleOutsideClick = event => {
      if (calendarWrapRef.current && !calendarWrapRef.current.contains(event.target)) {
        setOpenCalendar(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const getScrollTarget = useCallback((view = activeSummaryView) => {
    const root = pageRef.current;
    if (!root) return null;
    if (!view) return root;
    return root.querySelector('.historic-readonly-page .ogm-scroll-area') || root.querySelector('.historic-readonly-page') || root;
  }, [activeSummaryView]);

  const syncTimelineViewport = useCallback(() => {
    const timelineNode = timelineRef.current;
    if (!timelineNode) {
      setTimelineDate(flights[0]?.date_of_flight || '');
      return;
    }

    const timelineRect = timelineNode.getBoundingClientRect();
    const visibleStart = timelineRect.left + TIMELINE_DATE_MASK_WIDTH;
    const cards = Array.from(timelineNode.querySelectorAll('.historic-flight-card'));
    const firstVisibleCard = cards.find(card => {
      const cardRect = card.getBoundingClientRect();
      return cardRect.right > visibleStart + 1 && cardRect.left < timelineRect.right - 1;
    }) || cards[0];
    const firstVisibleFlight = flights.find(flight => String(flight.id) === firstVisibleCard?.dataset.flightId);
    const nextDate = firstVisibleFlight?.date_of_flight || flights[0]?.date_of_flight || '';

    setTimelineDate(current => current === nextDate ? current : nextDate);
  }, [flights]);

  const rememberCurrentScroll = () => {
    const scrollKey = activeSummaryView || 'summary';
    const scrollTarget = getScrollTarget(activeSummaryView);
    if (scrollTarget) scrollPositionsRef.current[scrollKey] = scrollTarget.scrollTop;
    if (timelineRef.current) scrollPositionsRef.current.timelineLeft = timelineRef.current.scrollLeft;
  };

  const openSummaryView = (view) => {
    rememberCurrentScroll();
    setActiveSummaryView(view);
  };

  const returnToSummary = () => {
    rememberCurrentScroll();
    setActiveSummaryView(null);
  };

  useLayoutEffect(() => {
    const scrollKey = activeSummaryView || 'summary';
    const scrollTarget = getScrollTarget(activeSummaryView);
    const timelineNode = timelineRef.current;
    if (!scrollTarget) return undefined;

    const previousBehavior = scrollTarget.style.scrollBehavior;
    scrollTarget.style.scrollBehavior = 'auto';
    scrollTarget.scrollTop = scrollPositionsRef.current[scrollKey] || 0;
    scrollTarget.style.scrollBehavior = previousBehavior;
    if (!activeSummaryView && timelineNode) {
      timelineNode.scrollLeft = scrollPositionsRef.current.timelineLeft || 0;
      window.requestAnimationFrame(syncTimelineViewport);
    }

    const rememberScroll = () => {
      scrollPositionsRef.current[scrollKey] = scrollTarget.scrollTop;
      if (!activeSummaryView && timelineNode) {
        scrollPositionsRef.current.timelineLeft = timelineNode.scrollLeft;
        syncTimelineViewport();
      }
    };

    scrollTarget.addEventListener('scroll', rememberScroll, { passive: true });
    if (!activeSummaryView && timelineNode) {
      timelineNode.addEventListener('scroll', rememberScroll, { passive: true });
    }
    return () => {
      rememberScroll();
      scrollTarget.removeEventListener('scroll', rememberScroll);
      if (timelineNode) {
        timelineNode.removeEventListener('scroll', rememberScroll);
      }
    };
  }, [activeSummaryView, getScrollTarget, syncTimelineViewport]);

  useEffect(() => {
    if (activeSummaryView) return undefined;
    const frame = window.requestAnimationFrame(syncTimelineViewport);
    return () => window.cancelAnimationFrame(frame);
  }, [activeSummaryView, flights, syncTimelineViewport]);

  useEffect(() => () => {
    if (timelineDragFrameRef.current) {
      window.cancelAnimationFrame(timelineDragFrameRef.current);
    }
  }, []);

  const updateFilter = (field, value) => setFilters(prev => ({ ...prev, [field]: value }));
  const clearFilters = () => {
    setFilters(defaultFilters);
    setOpenCalendar(null);
  };
  const toggleCalendar = fieldKey => setOpenCalendar(prev => prev === fieldKey ? null : fieldKey);
  const changeCalendarMonth = (fieldKey, delta) => {
    setCalendarMonths(prev => ({ ...prev, [fieldKey]: addMonths(prev[fieldKey], delta) }));
  };
  const selectCalendarDate = (fieldKey, day) => {
    updateFilter(fieldKey, format(day, 'yyyy-MM-dd'));
    setCalendarMonths(prev => ({ ...prev, [fieldKey]: startOfMonth(day) }));
    setOpenCalendar(null);
  };
  const clearCalendarDate = fieldKey => {
    updateFilter(fieldKey, '');
    setOpenCalendar(null);
  };
  const handleTimelineWheel = (event) => {
    const node = event.currentTarget;
    if (!node || node.scrollWidth <= node.clientWidth) return;
    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    if (!delta) return;
    node.scrollLeft += delta;
    event.preventDefault();
    window.requestAnimationFrame(syncTimelineViewport);
  };
  const handleTimelinePointerDown = (event) => {
    if (event.pointerType === 'touch' || event.button !== 0) return;
    timelineDragRef.current = {
      active: true,
      moved: false,
      startX: event.clientX,
      scrollLeft: event.currentTarget.scrollLeft
    };
    setTimelineDragging(false);
  };
  const handleTimelinePointerMove = (event) => {
    if (event.pointerType === 'touch') return;
    const drag = timelineDragRef.current;
    if (!drag.active) return;

    const delta = event.clientX - drag.startX;
    if (!drag.moved && Math.abs(delta) < TIMELINE_DRAG_THRESHOLD) return;

    drag.moved = true;
    setTimelineDragging(true);
    timelineDragTargetRef.current = {
      node: event.currentTarget,
      scrollLeft: drag.scrollLeft - delta
    };
    if (!timelineDragFrameRef.current) {
      timelineDragFrameRef.current = window.requestAnimationFrame(() => {
        const target = timelineDragTargetRef.current;
        if (target.node) {
          target.node.scrollLeft = target.scrollLeft;
          syncTimelineViewport();
        }
        timelineDragFrameRef.current = 0;
      });
    }
    event.preventDefault();
  };
  const finishTimelineDrag = (event) => {
    if (event.pointerType === 'touch') {
      window.requestAnimationFrame(syncTimelineViewport);
      return;
    }
    const drag = timelineDragRef.current;
    if (!drag.active) return;
    const scrollDistance = Math.abs((event.currentTarget?.scrollLeft || 0) - drag.scrollLeft);

    if (drag.moved && scrollDistance >= TIMELINE_CLICK_SUPPRESS_SCROLL_THRESHOLD) {
      suppressTimelineCardClickRef.current = true;
      window.setTimeout(() => {
        suppressTimelineCardClickRef.current = false;
      }, 120);
    }

    timelineDragRef.current = { active: false, moved: false, startX: 0, scrollLeft: 0 };
    setTimelineDragging(false);
    syncTimelineViewport();
  };
  const handleTimelineCardClick = (flightId) => {
    if (suppressTimelineCardClickRef.current) {
      suppressTimelineCardClickRef.current = false;
      return;
    }
    setSelectedId(flightId);
  };
  const handleTimelineCardPointerDown = () => {
    suppressTimelineCardClickRef.current = false;
  };
  const displayedTimelineDate = timelineDate || flights[0]?.date_of_flight;
  const hasActiveFilters = Boolean(filters.search || filters.date_from || filters.date_to);
  const renderReadOnlyPageHeader = (title) => (
    <div className="ogm-sub-header historic-readonly-header">
      <button className="ogm-back-btn" onClick={returnToSummary}>
        <img src={arrowLeftIcon} alt="" className="ogm-back-arrow app-icon app-icon-arrow-sm-left" />FLIGHT SUMMARY
      </button>
      <div className="ogm-page-title">{title}</div>
      <span className="historic-readonly-spacer" />
    </div>
  );

  const renderFlightDataPage = () => (
    <div className="historic-readonly-page ogm-container">
      {renderReadOnlyPageHeader('Flight Data')}
      <div className="ogm-scroll-area">
        <div className="fd-section">
          <div className="fd-row">
            <div className="fd-label">Date of Flight</div>
            <ReadOnlyInput className="fd fd-input" value={fmtDate(selectedFlight?.date_of_flight)} />
          </div>
          <div className="fd-row">
            <div className="fd-label">Flight Number</div>
            <ReadOnlyInput className="fd fd-input" value={selectedFlight?.flight_number || ''} />
          </div>
          <div className="fd-row">
            <div className="fd-label">From - To</div>
            <div className="fd-from-to">
              <ReadOnlyInput className="fd fd-input" value={selectedFlight?.from_airport || ''} />
              <span className="fd-separator">–</span>
              <ReadOnlyInput className="fd fd-input" value={selectedFlight?.to_airport || ''} />
            </div>
          </div>
        </div>

        <div className="fd-oooi-section">
          <div className="fd-paired-grid">
            <div className="fd-paired-cell">
              <div className="fd-label">Scheduled Time of Departure</div>
              <div className="fd-time-input-wrap fd-time-disabled"><ReadOnlyInput className="fd fd-time-input" value={fmtTime(selectedFlight?.scheduled_time_departure)} /></div>
            </div>
            <div className="fd-paired-cell fd-paired-cell-empty" aria-hidden="true" />
            <div className="fd-paired-cell">
              <div className="fd-label">Time Off-block</div>
              <div className="fd-time-input-wrap fd-time-disabled"><ReadOnlyInput className="fd fd-time-input" value={fmtTime(selectedFlight?.time_off_block)} /></div>
            </div>
            <div className="fd-paired-cell">
              <div className="fd-label">Time TakeOff</div>
              <div className="fd-time-input-wrap fd-time-disabled"><ReadOnlyInput className="fd fd-time-input" value={fmtTime(selectedFlight?.time_takeoff)} /></div>
            </div>
            <div className="fd-paired-cell">
              <div className="fd-label">Time On-block</div>
              <div className="fd-time-input-wrap fd-time-disabled"><ReadOnlyInput className="fd fd-time-input" value={fmtTime(selectedFlight?.time_on_block)} /></div>
            </div>
            <div className="fd-paired-cell">
              <div className="fd-label">Time Landing</div>
              <div className="fd-time-input-wrap fd-time-disabled"><ReadOnlyInput className="fd fd-time-input" value={fmtTime(selectedFlight?.time_landing)} /></div>
            </div>
          </div>

          <div className="fd-paired-grid fd-calc-grid">
            <div className="fd-paired-cell">
              <div className="fd-label">Block Time</div>
              <div className="fd-time-calc-box">{selectedFlight?.block_time ? minutesToText(selectedFlight.block_time) : ''}</div>
            </div>
            <div className="fd-paired-cell">
              <div className="fd-label">Flight Time</div>
              <div className="fd-time-calc-box">{selectedFlight?.flight_time ? minutesToText(selectedFlight.flight_time) : ''}</div>
            </div>
          </div>
        </div>

        <div className="fd-landing-section">
          <div className="fd-paired-grid">
            <div className="fd-paired-cell">
              <div className="fd-label">Full Stop Landing</div>
              <div className="fd-time-calc-box fd-time-calc-left">{selectedFlight?.full_stop_landings ?? 0}</div>
            </div>
            <div className="fd-paired-cell">
              <div className="fd-label">Push back this Sector</div>
              <div className="fuel-unit-toggle fuel-v2-unit-toggle fd-pushback-toggle historic-disabled-toggle">
                <button type="button" className={`toggle-option ${!selectedFlight?.pushback ? 'active' : ''}`} disabled>No</button>
                <button type="button" className={`toggle-option ${selectedFlight?.pushback ? 'active' : ''}`} disabled>Yes</button>
              </div>
            </div>
            <div className="fd-paired-cell">
              <div className="fd-label">Touch & Go</div>
              <div className="fd-time-calc-box fd-time-calc-left">{selectedFlight?.touch_and_go ?? 0}</div>
            </div>
            <div />
          </div>
          <div className="fd-paired-grid fd-single-left">
            <div className="fd-paired-cell">
              <div className="fd-label">Total Landings</div>
              <div className="fd-time-calc-box fd-time-calc-left">{selectedFlight?.total_landings ?? 0}</div>
            </div>
            <div />
          </div>
        </div>

        {hasDelay ? (
          <div className="fd-delay-card">
            <div className="fd-delay-grid">
              {delays.map((delay, index) => (
                <React.Fragment key={delay.id || index}>
                  <div className="fd-delay-column">
                    <div className="fd-delay-field">
                      <label className="fd-label fd-delay-label">Delay</label>
                      <div className="fd-time-calc-box fd-time-calc-left">
                        {formatDelayReason(delay)}
                      </div>
                    </div>
                    <div className="fd-delay-field">
                      <label className="fd-label fd-delay-label">Calculated Total Delay</label>
                      <div className="fd-time-calc-box fd-time-calc-left">{minutesToText(calculatedDepartureDelayMinutes)}</div>
                    </div>
                  </div>
                  <div className="fd-delay-column">
                    <div className="fd-delay-field">
                      <label className="fd-label fd-delay-label">Delay Time</label>
                      <div className="fd-time-calc-box fd-time-calc-left">{minutesToText(delay.delay_minutes)}</div>
                    </div>
                    <div className="fd-delay-field">
                      <label className="fd-label fd-delay-label">Entered Total Delay</label>
                      <div className="fd-time-calc-box fd-time-calc-left">{minutesToText(delay.delay_minutes)}</div>
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );

  const renderFuelPage = () => (
    <div className="historic-readonly-page ogm-container">
      {renderReadOnlyPageHeader('Fuel Page')}
      <div className="ogm-scroll-area ogm-fuel-page fuel-v2-page">
        <div className="fuel-card fuel-v2-top-card">
          <div className="fuel-v2-grid fuel-v2-grid-top">
            <div className="fuel-v2-field">
              <div className="fd-label">Date/Time</div>
              <ReadOnlyInput className="fuel-date-input" value={fmtDateTime(fuelData?.created_at || latestFuel?.created_at || selectedFlight?.date_of_flight)} />
            </div>
            <div className="fuel-v2-field">
              <div className="fd-label">Remaining Fuel</div>
              <ReadOnlyInput className="fuel-cell-input fuel-v2-cell" value={formatFuelValue(num(fuelData?.remaining_left) + num(fuelData?.remaining_center) + num(fuelData?.remaining_right) + num(fuelData?.remaining_other))} suffix="kg" />
            </div>
          </div>
        </div>

        <div className="fuel-uplifts-section expanded">
          {(fuelUplifts.length ? fuelUplifts : [null]).map((uplift, index) => {
            const actualUpliftKg = num(uplift?.bowser_liters) * num(uplift?.density || 0.8);
            const departureFuelTotal = fuelTotal(uplift);
            const remainingTotal = num(fuelData?.remaining_left) + num(fuelData?.remaining_center) + num(fuelData?.remaining_right) + num(fuelData?.remaining_other);
            const fuelAfterUpliftKg = remainingTotal + actualUpliftKg;
            const calculatedDifference = departureFuelTotal ? Math.abs(fuelAfterUpliftKg - departureFuelTotal) : 0;
            return (
              <div key={uplift?.id || index} className="fuel-card fuel-v2-uplift-card">
                <div className="fuel-card-title">
                  <span className="ogm-icon-placeholder" aria-hidden="true"><img src={fuelUpliftIcon} alt="" className="ogm-icon-img app-icon app-icon-fueluplift" /></span>
                  <span>Fuel Uplift {index + 1}</span>
                </div>
                <div className="fuel-v2-grid">
                  <div className="fuel-v2-field">
                    <div className="fd-label">Fuel Type</div>
                    <div className="fuel-v2-cell fuel-v2-plain-cell historic-readonly-cell">{uplift?.fuel_type || ''}</div>
                  </div>
                  <div className="fuel-v2-field fuel-v2-unit-field">
                    <div className="fuel-unit-toggle fuel-v2-unit-toggle historic-disabled-toggle">
                      <button type="button" className={`toggle-option ${(uplift?.fuel_unit || 'metric') === 'metric' ? 'active' : ''}`} disabled>Metric</button>
                      <button type="button" className={`toggle-option ${(uplift?.fuel_unit || 'metric') === 'imperial' ? 'active' : ''}`} disabled>Imperial</button>
                    </div>
                  </div>
                </div>
                <div className="fuel-divider" />
                <div className="fuel-v2-grid">
                  <div className="fuel-v2-field">
                    <div className="fd-label">Density</div>
                    <ReadOnlyInput className="fuel-v2-cell fuel-v2-plain-cell fuel-v2-value-cell" value={uplift?.density || ''} suffix="kg/l" />
                  </div>
                  <div className="fuel-v2-field" />
                  <div className="fuel-v2-field">
                    <div className="fd-label">Actual Uplift</div>
                    <ReadOnlyInput className="fuel-cell-input fuel-v2-cell" value={uplift?.bowser_liters || ''} suffix={(uplift?.fuel_unit || 'metric') === 'imperial' ? 'gal' : 'l'} />
                  </div>
                  <div className="fuel-v2-field">
                    <div className="fd-label">Actual Uplift</div>
                    <div className="fuel-total-box fuel-v2-total-box">{formatFuelValue(actualUpliftKg)} <span>kg</span></div>
                  </div>
                </div>
                <div className="fuel-divider" />
                <div className="fuel-v2-grid fuel-v2-grid-summary">
                  <div className="fuel-v2-field" />
                  <div className="fuel-v2-field">
                    <div className="fd-label">Fuel after Uplift</div>
                    <div className="fuel-total-box fuel-v2-total-box">{formatFuelValue(fuelAfterUpliftKg)} <span>kg</span></div>
                  </div>
                  <div className="fuel-v2-field">
                    <div className="fd-label">Calculated Difference</div>
                    <div className="fuel-total-box fuel-v2-total-box">{formatFuelValue(calculatedDifference)} <span>kg</span></div>
                  </div>
                  <div className="fuel-v2-field">
                    <div className="fd-label">Departure Fuel</div>
                    <ReadOnlyInput className="fuel-cell-input fuel-v2-cell" value={departureFuelTotal ? formatFuelValue(departureFuelTotal) : ''} suffix="kg" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderOilPage = () => (
    <div className="historic-readonly-page ogm-container">
      {renderReadOnlyPageHeader('Before Flight Oil Data')}
      <div className="ogm-scroll-area">
        <div className="oil-card">
          <div className="oil-card-title">
            <img src={oilIcon} alt="" className="oil-card-title-icon app-icon app-icon-oil" />
            <span>Oil Before Flight</span>
          </div>
          <div className="oil-grid">
            <div className="oil-field">
              <div className="oil-label">Date/Time</div>
              <div className="oil-required-wrap complete"><ReadOnlyInput className="fuel-date-input oil-date-input" value={fmtDateTime(oil?.created_at || selectedFlight?.date_of_flight)} /></div>
            </div>
            <div className="oil-field">
              <div className="oil-label">Engine 1</div>
              <div className="oil-required-wrap complete"><ReadOnlyInput className="oil-suffix-wrap oil-qty-input" value={oil?.eng1_oil_added || ''} suffix="qt" /></div>
            </div>
            <div className="oil-field">
              <div className="oil-label">Engine 2</div>
              <div className="oil-required-wrap complete"><ReadOnlyInput className="oil-suffix-wrap oil-qty-input" value={oil?.eng2_oil_added || ''} suffix="qt" /></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDeicingPage = () => (
    <div className="historic-readonly-page ogm-container">
      {renderReadOnlyPageHeader('De-Icing/Anti-Icing - Ground Mode')}
      <div className="ogm-scroll-area deicing-scroll-area">
        {(deicingRecords.length ? deicingRecords : [selectedDetail?.deicing]).filter(Boolean).map((record, index) => (
          <div className="deicing-record-card" key={record?.id || index}>
            <div className="deicing-record-title">
              <div className="deicing-record-title-left">
                <img src={deicingIcon} alt="" className="deicing-record-title-icon app-icon app-icon-deicing" />
                <span>Record {index + 1}</span>
              </div>
            </div>
            <div className="deicing-grid">
              <div className="deicing-field deicing-field-wide">
                <label className="deicing-label">Fluid Type</label>
                <div className="deicing-required-wrap fuel-v2-cell fuel-v2-plain-cell historic-readonly-cell">{record?.fluid_type || ''}</div>
              </div>
              <div className="deicing-field">
                <label className="deicing-label">Mixture</label>
                <ReadOnlyInput className="deicing-required-wrap fuel-v2-cell" value={record?.mixture || ''} suffix="%" />
              </div>
              <div className="deicing-field">
                <label className="deicing-label">Quantity</label>
                <ReadOnlyInput className="deicing-required-wrap fuel-v2-cell" value={record?.quantity || ''} suffix="l" />
              </div>
              <div className="deicing-field">
                <label className="deicing-label">Start UTC</label>
                <ReadOnlyInput className="deicing-required-wrap fuel-v2-cell" value={record?.start_utc || ''} />
              </div>
              <div className="deicing-field">
                <label className="deicing-label">HOT Min</label>
                <ReadOnlyInput className="deicing-required-wrap fuel-v2-cell" value={record?.holdover_min || ''} />
              </div>
              <div className="deicing-field">
                <label className="deicing-label">HOT Max</label>
                <ReadOnlyInput className="deicing-required-wrap fuel-v2-cell" value={record?.holdover_max || ''} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAutolandPage = () => (
    <div className="historic-readonly-page ogm-container">
      {renderReadOnlyPageHeader('Autoland Check')}
      <div className="ogm-scroll-area">
        <div className="autoland-section-card">
          <div className="autoland-section-title">General</div>
          <div className="autoland-grid autoland-grid-general">
            <div className="autoland-field">
              <label className="autoland-label">A/C Reg / Flight Number</label>
              <div className="autoland-readonly-box">{autolandRegAndFlight}</div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">Airport</label>
              <div className="autoland-readonly-box">{autolandAirport}</div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">Captain</label>
              <div className="autoland-readonly-box">{captainName}</div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">Date</label>
              <div className="autoland-readonly-box">{autolandDate}</div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">Time</label>
              <div className={getAutolandFieldClass(autolandTimeIsValid)}>
                <input type="text" className="form-input autoland-input" readOnly value={autolandTimeValue} />
              </div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">First Officer</label>
              <div className="autoland-readonly-box">{firstOfficerName}</div>
            </div>
          </div>
        </div>

        <div className="autoland-section-card">
          <div className="autoland-section-title">Data</div>
          <div className="autoland-grid autoland-grid-data">
            <div className="autoland-field">
              <label className="autoland-label">Runway</label>
              <div className={getAutolandFieldClass(autolandRunwayIsValid)}>
                <input type="text" className="form-input autoland-input" readOnly value={autoland?.runway || ''} />
              </div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">Wind</label>
              <div className={getAutolandFieldClass(autolandWindIsValid)}>
                <input type="text" className="form-input autoland-input" readOnly value={autoland?.wind || ''} />
              </div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">QNH</label>
              <div className={getAutolandFieldClass(autolandQnhIsValid)}>
                <input type="text" className="form-input autoland-input" readOnly value={autoland?.qnh || ''} />
              </div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">Temp</label>
              <div className={getAutolandFieldClass(autolandTempIsValid)}>
                <input type="text" className="form-input autoland-input" readOnly value={autoland?.temp || ''} />
              </div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">VIS/RVR</label>
              <div className={getAutolandFieldClass(hasValue(autoland?.vis_rvr))}>
                <input type="text" className="form-input autoland-input" readOnly value={autoland?.vis_rvr || ''} />
              </div>
            </div>
            <div className="autoland-field">
              <label className="autoland-label">Reasons</label>
              <div className={getAutolandFieldClass(hasValue(autoland?.reasons))}>
                <input type="text" className="form-input autoland-input" readOnly value={autoland?.reasons || ''} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderArrivalFuelPage = () => (
    <div className="historic-readonly-page ogm-container">
      {renderReadOnlyPageHeader('Arrival Fuel')}
      <div className="ogm-scroll-area">
        <div className="arrival-fuel-card arrival-fuel-card-with-burnt">
          <div className="arrival-fuel-grid arrival-fuel-grid-with-burnt">
            <label className="arrival-fuel-label">Date/ Time</label>
            <ReadOnlyInput className="arrival-fuel-input" value={fmtDateTime(arrivalFuel?.created_at || selectedFlight?.time_on_block || selectedFlight?.date_of_flight)} />
            <label className="arrival-fuel-label arrival-fuel-depart-label">Depart. Fuel</label>
            <div className="arrival-fuel-readonly">
              {latestFuel ? formatFuelValue(fuelTotal(latestFuel)) : '--'} <span>kg</span>
            </div>
            <label className="arrival-fuel-label arrival-fuel-burnt-label">Fuel Burnt</label>
            <ReadOnlyInput className="arrival-fuel-input-wrap arrival-fuel-burnt-input-wrap" value={arrivalFuel?.fuel_burnt == null ? '--' : formatFuelValue(arrivalFuel.fuel_burnt)} suffix="kg" />
            <label className="arrival-fuel-label arrival-fuel-arrival-label">Arrival Fuel</label>
            <ReadOnlyInput className="arrival-fuel-input-wrap" value={formatFuelValue(fuelTotal(arrivalFuel))} suffix="kg" />
          </div>
        </div>
      </div>
    </div>
  );

  const activeSummaryContent = {
    flightData: renderFlightDataPage,
    fuel: renderFuelPage,
    oil: renderOilPage,
    deicing: renderDeicingPage,
    autoland: renderAutolandPage,
    arrivalFuel: renderArrivalFuelPage
  };

  return (
    <div className="historic-page" ref={pageRef}>
      {activeSummaryView && activeSummaryContent[activeSummaryView] ? (
        <React.Fragment key={activeSummaryView}>
          {activeSummaryContent[activeSummaryView]()}
        </React.Fragment>
      ) : (
        <div className="historic-summary-view historic-view-shell" key="summary">
          <section className="historic-filter-strip">
            <label>Filter</label>
            <input
              className="historic-input historic-filter-search"
              value={filters.search}
              onChange={e => updateFilter('search', e.target.value)}
              placeholder="Search for..."
            />
            <label>Date</label>
            <div className="historic-date-selectors" ref={calendarWrapRef}>
              <HistoricDateSelector
                fieldKey="date_from"
                label="From"
                value={filters.date_from}
                isOpen={openCalendar === 'date_from'}
                visibleMonth={calendarMonths.date_from}
                onToggle={toggleCalendar}
                onChangeMonth={changeCalendarMonth}
                onSelectDate={selectCalendarDate}
                onClear={clearCalendarDate}
              />
              <span className="historic-date-dash">–</span>
              <HistoricDateSelector
                fieldKey="date_to"
                label="To"
                value={filters.date_to}
                isOpen={openCalendar === 'date_to'}
                visibleMonth={calendarMonths.date_to}
                onToggle={toggleCalendar}
                onChangeMonth={changeCalendarMonth}
                onSelectDate={selectCalendarDate}
                onClear={clearCalendarDate}
              />
            </div>
            {hasActiveFilters ? (
              <button type="button" className="historic-clear-btn" onClick={clearFilters} aria-label="Clear filters">×</button>
            ) : null}
          </section>

          <section className="historic-timeline">
            <div
              className={`historic-timeline-scroll ${timelineDragging ? 'dragging' : ''}`}
              ref={timelineRef}
              onWheel={handleTimelineWheel}
              onPointerDown={handleTimelinePointerDown}
              onPointerMove={handleTimelinePointerMove}
              onPointerUp={finishTimelineDrag}
              onPointerCancel={finishTimelineDrag}
              onPointerLeave={finishTimelineDrag}
            >
              <div className="historic-timeline-track">
                <div className="historic-timeline-line" />
                <div className="historic-flight-cards">
                  {flights.map(flight => {
                    const statusInfo = getHistoricStatusInfo(flight.status);
                    return (
                      <button
                        key={flight.id}
                        type="button"
                        data-flight-id={flight.id}
                        className={`historic-flight-card ${selectedId === flight.id ? 'active' : ''}`}
                        onPointerDown={handleTimelineCardPointerDown}
                        onClick={() => handleTimelineCardClick(flight.id)}
                      >
                        <div className="historic-flight-card-top">
                          {flight.flight_number || flight.id}
                        </div>
                        <div className="historic-flight-route">
                          <span>{flight.from_airport || '----'} - {flight.to_airport || '----'}</span>
                          <span>{fmtDateTime(flight.date_of_flight)}</span>
                        </div>
                        <div className="historic-flight-card-status">
                          <span className={`historic-status-chip ${statusInfo.className}`}>{statusInfo.label}</span>
                        </div>
                      </button>
                    );
                  })}
                  {!loading && flights.length === 0 ? <div className="historic-empty historic-timeline-empty">No flights match this filter.</div> : null}
                </div>
              </div>
            </div>
            <div className="historic-date-mask" aria-hidden="true" />
            <div className="historic-date-bubble">{fmtShortDate(displayedTimelineDate)}</div>
          </section>

          <main className="historic-summary-area">
            <div className="historic-summary-head">
              <h2>Flight Summary</h2>
            </div>

            {detailLoading ? (
              <div className="historic-empty">Loading flight summary...</div>
            ) : selectedFlight ? (
              <div className="historic-workstep-stack">
                <SummaryTask label="Flight Data" iconSrc={flightDataIcon} iconClassName="app-icon-flightdata historic-icon-flight-data" isComplete={hasFlightData} statusText={!hasFlightData ? 'Incomplete Flight Data' : ''} onClick={() => openSummaryView('flightData')} />
                <HistoricSignatureCard title="Accept Aircraft" event={acceptanceEvent} fallbackDate={selectedFlight.date_of_flight} fallbackUser={flightUserName} alt="Commander signature" />
                <SummaryTask label="Fuel Uplift" iconSrc={fuelUpliftIcon} iconClassName="app-icon-fueluplift historic-icon-fuel-uplift" isComplete={hasFuel} statusText={!hasFuel ? 'Incomplete Fuel Uplift Data' : `${fuelUplifts.length} record${fuelUplifts.length === 1 ? '' : 's'}`} onClick={() => openSummaryView('fuel')} />
                <SummaryTask label="Before Flight Oil Data" iconSrc={oilIcon} iconClassName="app-icon-oil historic-icon-before-oil" isComplete={hasOil} statusText={!hasOil ? 'Incomplete Pre-Flight Oil Data' : ''} onClick={() => openSummaryView('oil')} />
                {hasDeicing ? <SummaryTask label="De-Icing/Anti-Icing" iconSrc={deicingIcon} iconClassName="app-icon-deicing historic-icon-deicing" isComplete statusText={`${deicingRecords.length || 1} record${(deicingRecords.length || 1) === 1 ? '' : 's'}`} onClick={() => openSummaryView('deicing')} /> : null}
                <HistoricSignatureCard title="Close Flight" event={closeFlightEvent} fallbackDate={selectedFlight.time_on_block || selectedFlight.date_of_flight} fallbackUser={flightUserName} alt="Commander closure signature" />
                {hasAutoland ? <SummaryTask label="Autoland" iconSrc={flightDataIcon} iconClassName="app-icon-flightdata historic-icon-autoland" isComplete statusText={String(autoland.status || '').toUpperCase()} onClick={() => openSummaryView('autoland')} /> : null}
                {hasArrivalFuel ? <SummaryTask label="Arrival Fuel" iconSrc={fuelUpliftIcon} iconClassName="app-icon-fueluplift historic-icon-arrival-fuel" isComplete statusText={`${formatFuelValue(fuelTotal(arrivalFuel))} kg`} onClick={() => openSummaryView('arrivalFuel')} /> : null}
                {hasDeicing ? <SummaryTask label="De-Icing/Anti-Icing" iconSrc={deicingIcon} iconClassName="app-icon-deicing historic-icon-close-deicing" isComplete statusText={`${deicingRecords.length || 1} record${(deicingRecords.length || 1) === 1 ? '' : 's'}`} onClick={() => openSummaryView('deicing')} /> : null}
              </div>
            ) : (
              <div className="historic-empty">Select a flight to view details.</div>
            )}
          </main>
        </div>
      )}
    </div>
  );
};

export default HistoricAircraftData;
